package com.evoapps.evoshift.ui.workplaces

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.domain.model.GeoLocation

/**
 * Finds the coordinates of an address with the system's own geocoder (SH-110): Android's
 * `Geocoder`, Apple's `CLGeocoder`. No API key, no location permission: only the address the
 * manager typed is looked up. The web and the desktop have none; there, and whenever the phone's
 * finds nothing, OpenStreetMap answers through the geocodeAddress function (SH-132).
 */
fun interface AddressGeocoder {
    suspend fun locate(address: String): GeocodeResult
}

sealed interface GeocodeResult {
    data class Found(val location: GeoLocation) : GeocodeResult
    /** The geocoder knows no such address. */
    data object NotFound : GeocodeResult
    /** No answer: offline, the service is down or refused. */
    data object Failed : GeocodeResult
}

/** Replaces the system geocoder where there is none to call (UI tests). */
val LocalAddressGeocoderOverride = staticCompositionLocalOf<AddressGeocoder?> { null }

/** This platform's geocoder; null where there is none. */
@Composable
expect fun rememberPlatformAddressGeocoder(): AddressGeocoder?

/** The [LocalAddressGeocoderOverride], else this platform's geocoder, else null (OpenStreetMap only). */
@Composable
fun rememberAddressGeocoder(): AddressGeocoder? =
    LocalAddressGeocoderOverride.current ?: rememberPlatformAddressGeocoder()

/** OpenStreetMap at [location] (or searching [address]): a page the manager opens to check or find the point. */
fun mapLink(location: GeoLocation?, address: String): String? = when {
    location != null -> location.rounded().let {
        "https://www.openstreetmap.org/?mlat=${it.latitude}&mlon=${it.longitude}#map=18/${it.latitude}/${it.longitude}"
    }
    address.isNotBlank() -> "https://www.openstreetmap.org/search?query=" + encodeQuery(address.trim())
    else -> null
}

/** Percent-encodes [text] for a URL query (UTF-8; unreserved characters stay). */
internal fun encodeQuery(text: String): String = buildString {
    text.encodeToByteArray().forEach { byte ->
        val c = byte.toInt().toChar()
        if (byte >= 0 && (c.isLetterOrDigit() || c in "-_.~")) append(c)
        else append('%').append(((byte.toInt() and 0xFF) + 0x100).toString(16).substring(1).uppercase())
    }
}
